import React, { useState, useMemo } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Chip,
  InputAdornment,
  Pagination
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import LockResetIcon from '@mui/icons-material/LockReset';
import { 
  usePostUsersMutation,
  useGetUsersQuery, 
  usePostPasswordResetRequestByUserEmailMutation 
} from '../../redux/residentPortalApi';
import { useHasAnyRole } from '../../hooks/useHasAnyRole';

// Type for sorting
type Order = 'asc' | 'desc';
type OrderBy = 'name' | 'email' | 'roles';

const Users = () => {
  const canManageUsers = useHasAnyRole(['residents-support']);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: 'password',
    repeatPassword: 'password',
  });
  const [createUserError, setCreateUserError] = useState<string | null>(null);
  const [createUserSuccess, setCreateUserSuccess] = useState<string | null>(null);

  // State for search functionality
  const [searchTerm, setSearchTerm] = useState<string>('');
  
  // State for password reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ id?: string, email: string, name?: string } | null>(null);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Sorting states
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<OrderBy>('name');
  
  // Pagination states
  const [page, setPage] = useState(1);
  const rowsPerPage = 10

  // Fetch users data
  const { data: users, isLoading, refetch } = useGetUsersQuery();
  
  // Password reset mutation
  const [requestPasswordReset, { isLoading: isResetting }] = usePostPasswordResetRequestByUserEmailMutation();
  const [createUser, { isLoading: isCreatingUser }] = usePostUsersMutation();

  const handleNewUserChange = (field: 'name' | 'email' | 'password' | 'repeatPassword') =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewUser((previousState) => ({ ...previousState, [field]: event.target.value }));
      setCreateUserError(null);
      setCreateUserSuccess(null);
    };

  const handleCreateUser = async () => {
    if (!canManageUsers) {
      setCreateUserError('You do not have permission to register users.');
      return;
    }

    if (!newUser.email || !newUser.password) {
      setCreateUserError('Email and password are required.');
      return;
    }

    if (newUser.password !== newUser.repeatPassword) {
      setCreateUserError('Passwords do not match.');
      return;
    }

    try {
      await createUser({
        userWithPassword: {
          name: newUser.name.trim() || undefined,
          email: newUser.email.trim(),
          password: newUser.password,
        },
      }).unwrap();

      setCreateUserSuccess(`User ${newUser.email.trim()} has been registered.`);
      setNewUser({
        name: '',
        email: '',
        password: 'password',
        repeatPassword: 'password',
      });
      refetch();
    } catch (error) {
      console.error('Failed to create user:', error);
      setCreateUserError('Failed to register user. Please verify details and try again.');
    }
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };
  
  // Function to handle sorting
  const handleRequestSort = (property: OrderBy) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };
  
  // Function to handle pagination
  const handleChangePage = (_event: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    if (!users) return [];
    
    // First, filter the users
    let filtered = users.filter(user => 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.name && user.name.toLowerCase().includes(searchTerm.toLowerCase()))
    );
    
    // Then, sort the filtered users
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (orderBy) {
        case 'name':
          const nameA = a.name || '';
          const nameB = b.name || '';
          comparison = nameA.localeCompare(nameB);
          break;
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'roles':
          const rolesA = a.roles?.join(',') || '';
          const rolesB = b.roles?.join(',') || '';
          comparison = rolesA.localeCompare(rolesB);
          break;
        default:
          break;
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [users, searchTerm, order, orderBy]);
  
  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredAndSortedUsers.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredAndSortedUsers, page, rowsPerPage]);

  // Handle password reset request
  const handlePasswordResetRequest = (user: { id?: string, email: string, name?: string }) => {
    setSelectedUser(user);
    setResetDialogOpen(true);
    setResetSuccess(false);
  };

  // Confirm and send password reset
  const confirmPasswordReset = async () => {
    if (selectedUser) {
      try {
        await requestPasswordReset({userEmail: selectedUser.email}).unwrap();
        setResetSuccess(true);
      } catch (error) {
        console.error('Failed to send password reset:', error);
      }
    }
  };

  // Close the reset dialog
  const handleCloseResetDialog = () => {
    setResetDialogOpen(false);
    setSelectedUser(null);
  };

  // Format roles for display
  const formatRoles = (roles?: string[]) => {
    if (!roles || roles.length === 0) return 'No roles';
    
    return roles.map(role => (
      <Chip 
        key={role} 
        label={role} 
        size="small" 
        color={role === 'admin' ? 'primary' : 'default'}
        sx={{ mr: 0.5, mb: 0.5 }}
      />
    ));
  };

  return (
    <Container>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          User Management
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage site users, send password reset links, and view user roles.
        </Typography>
      </Box>

      <Card elevation={3} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Register New User
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Users with roles admin and residents-support can register users and initiate password resets.
          </Typography>

          {!canManageUsers && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              You do not have permission to register users.
            </Alert>
          )}

          {createUserError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createUserError}
            </Alert>
          )}

          {createUserSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {createUserSuccess}
            </Alert>
          )}

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
              gap: 2,
            }}
          >
            <TextField
              label="Name"
              value={newUser.name}
              onChange={handleNewUserChange('name')}
              fullWidth
            />
            <TextField
              label="Email"
              type="email"
              value={newUser.email}
              onChange={handleNewUserChange('email')}
              fullWidth
            />
            <TextField
              label="Password"
              type="password"
              value={newUser.password}
              onChange={handleNewUserChange('password')}
              fullWidth
            />
            <TextField
              label="Repeat Password"
              type="password"
              value={newUser.repeatPassword}
              onChange={handleNewUserChange('repeatPassword')}
              fullWidth
            />
          </Box>

          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleCreateUser}
              disabled={isCreatingUser || !canManageUsers}
              startIcon={isCreatingUser ? <CircularProgress size={20} /> : null}
            >
              Register User
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Paper elevation={3} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexGrow: 1 }}>
            <TextField
              placeholder="Search users..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ width: '300px' }}
            />
            <Typography variant="body2" color="text.secondary">
              {filteredAndSortedUsers.length} {filteredAndSortedUsers.length === 1 ? 'user' : 'users'} found
            </Typography>
          </Box>
          <Box>
            <Tooltip title="Refresh user list">
              <IconButton onClick={() => refetch()} color="primary">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Paper>

      <Card elevation={3}>
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: '70vh', boxShadow: 'none' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'name'}
                        direction={orderBy === 'name' ? order : 'asc'}
                        onClick={() => handleRequestSort('name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'email'}
                        direction={orderBy === 'email' ? order : 'asc'}
                        onClick={() => handleRequestSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      <TableSortLabel
                        active={orderBy === 'roles'}
                        direction={orderBy === 'roles' ? order : 'asc'}
                        onClick={() => handleRequestSort('roles')}
                      >
                        Roles
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedUsers && paginatedUsers.length > 0 ? (
                    paginatedUsers.map((user) => (
                      <TableRow 
                        key={user.id}
                        sx={{ 
                          '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                          '&:hover': { backgroundColor: '#f1f1f1' }
                        }}
                      >
                        <TableCell>{user.name || 'N/A'}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{formatRoles(user.roles)}</TableCell>
                        <TableCell align="right">
                          <Tooltip title="Send password reset link">
                            <IconButton 
                              onClick={() => handlePasswordResetRequest(user)}
                              color="primary"
                              size="small"
                              disabled={!canManageUsers}
                            >
                              <LockResetIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {searchTerm ? 'No users match your search' : 'No users found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
        {filteredAndSortedUsers.length > 0 && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            p: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5'
          }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * rowsPerPage) + 1} to {Math.min(page * rowsPerPage, filteredAndSortedUsers.length)} of {filteredAndSortedUsers.length} users
            </Typography>
            <Pagination 
              count={Math.ceil(filteredAndSortedUsers.length / rowsPerPage)} 
              page={page} 
              onChange={handleChangePage}
              color="primary"
              showFirstButton
              showLastButton
              size="medium"
              shape="rounded"
            />
          </Box>
        )}
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={resetDialogOpen} onClose={handleCloseResetDialog}>
        <DialogTitle>
          {resetSuccess ? "Password Reset Link Sent" : "Send Password Reset Link"}
        </DialogTitle>
        <DialogContent>
          {resetSuccess ? (
            <DialogContentText>
              A password reset link has been sent to {selectedUser?.email}.
            </DialogContentText>
          ) : (
            <DialogContentText>
              Are you sure you want to send a password reset link to {selectedUser?.email}?
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          {resetSuccess ? (
            <Button onClick={handleCloseResetDialog} color="primary">
              Close
            </Button>
          ) : (
            <>
              <Button onClick={handleCloseResetDialog} color="inherit">
                Cancel
              </Button>
              <Button 
                onClick={confirmPasswordReset} 
                color="primary" 
                disabled={isResetting}
                startIcon={isResetting ? <CircularProgress size={20} /> : null}
              >
                Send Reset Link
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Users;